import { useData, useRuntime } from "../runtime/provider";
import { AuthCredential } from "@components/SocialButton";
import { GoogleSignin } from "@react-native-google-signin/google-signin";
import {
  useAuthCreateUserWithEmailAndPassword,
  useAuthSignInWithCredential,
  useAuthSignInWithCustomToken,
  useAuthSignInWithEmailAndPassword,
} from "@utils/firebaseAuth/query/auth";
import { AuthError, GoogleAuthProvider, OAuthCredential } from "firebase/auth";
import { useAuth } from "@utils/firebaseAuth/auth";
import { postAuthDestination } from "@utils/postAuthDestination";
import { auth } from "@utils/firebase";
import { useCallback, useEffect, useRef, useState } from "react";
import { router, useNavigationContainerRef } from "expo-router";
import { Platform } from "react-native";
import { captureException } from "@utils/captureException";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";

import { toast } from "sonner-native";
import { useOnboardingStore } from "@components/Onboarding/store/onboardingStore";
import {
  clearPendingEmailVerificationToken,
  getPendingEmailVerificationToken,
} from "@utils/emailVerification/pendingToken";

const getAuthErrorCode = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "code" in error &&
  typeof error.code === "string"
    ? error.code
    : "unknown";

const captureAuthBreadcrumb = (
  error: unknown,
  operation: string,
  extra?: Record<string, unknown>,
) => {
  captureException(error, {
    channel: "breadcrumb",
    area: "auth",
    operation,
    tags: {
      code: getAuthErrorCode(error),
      platform: Platform.OS,
    },
    extra,
  });
};

const captureAuthError = (
  error: unknown,
  operation: string,
  extra?: Record<string, unknown>,
) => {
  captureException(error, {
    channel: "error",
    area: "auth",
    operation,
    tags: {
      code: getAuthErrorCode(error),
      platform: Platform.OS,
    },
    extra,
  });
};

const useLoginRegister = (
  isBottomSheetLogin = false,
  onLoginSuccess?: () => Promise<void>,
) => {
  const session = useData();
  const { kv } = useRuntime().ports;
  const authFlowStartedAtRef = useRef<number | null>(null);
  const isSetPasswordPath = useNavigationStore(
    (state) => state.isSetPasswordPath,
  );
  const lastAuthEndpointErrorRef = useRef<unknown>(null);

  // Cold-start race guard: when the app launches via a deep link (e.g. an
  // unlock link → /access/[id]), the LoginGate briefly bounces to /login
  // while Firebase auth resolves. Once status flips to "logged-in", the
  // useEffect below would call router.replace, but if expo-router's
  // navigationRef.isReady() is still false (assertIsReady guard in
  // expo-router/global-state/routing.js) the call throws
  // "Attempted to navigate before mounting the Root Layout component" and
  // the app crashes on cold start.
  //
  // useRootNavigationState() returns a state with a key BEFORE
  // navigationRef.isReady() flips true — using it as a gate isn't enough
  // (this was the v1 fix and it still crashed). Use the actual ref the
  // assertIsReady check reads from, and poll it until ready.
  const navRef = useNavigationContainerRef();
  const [isNavReady, setIsNavReady] = useState(
    () => navRef?.isReady?.() ?? false,
  );
  useEffect(() => {
    if (isNavReady) return;
    let rafId: number;
    const tick = () => {
      if (navRef?.isReady?.()) {
        setIsNavReady(true);
      } else {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isNavReady, navRef]);

  // Has to be a callback otherwise we get rerender loop
  const showAuthEndpointErrorToast = useCallback((error?: unknown) => {
    toast.error("Something went wrong, please try again later.");
    if (error && error !== lastAuthEndpointErrorRef.current) {
      lastAuthEndpointErrorRef.current = error;
      captureAuthError(error, "auth-endpoint-user-sync");
    }
  }, []);

  const { status, isSignup, onboardingStep } = useAuth({
    onError: showAuthEndpointErrorToast,
  });

  const logAuthTiming = useCallback(
    (step: string, extra?: Record<string, unknown>) => {
      const nowMs = globalThis.performance?.now?.() ?? Date.now();
      const startedAt = authFlowStartedAtRef.current;
      console.log("::AuthTiming::", step, {
        elapsedMs: startedAt ? Math.round(nowMs - startedAt) : undefined,
        status,
        onboardingStep,
        ...extra,
      });
    },
    [onboardingStep, status],
  );

  const emailSignInErrorHandler = useCallback((error: AuthError) => {
    if (error.code === "auth/invalid-email") {
      toast.error("That email address is invalid");
      captureAuthBreadcrumb(error, "email-sign-in-invalid-email");
      return;
    }
    if (
      error.code === "auth/invalid-login-credentials" ||
      error.code === "auth/invalid-credential" ||
      error.code === "auth/wrong-password" ||
      error.code === "auth/user-not-found"
    ) {
      toast.error("Invalid email or password", {
        description:
          "If you signed up with Apple or Google, try those instead.",
      });
      captureAuthBreadcrumb(error, "email-sign-in-invalid-credentials");
      return;
    }
    if (error.code === "auth/too-many-requests") {
      toast.error("Too many tries", {
        description: "Please try again later.",
      });
      captureAuthBreadcrumb(error, "email-sign-in-too-many-requests");
      return;
    }
    toast.error("Something went wrong, please try again later.");
    captureAuthError(error, "email-sign-in");
  }, []);

  const loginMutation = useAuthSignInWithEmailAndPassword(auth, {
    onMutate: () => {
      logAuthTiming("firebase-login-start");
    },
    onError: (error) => {
      logAuthTiming("firebase-login-error", { code: error.code });
      emailSignInErrorHandler(error);
    },
    onSuccess: async () => {
      logAuthTiming("firebase-login-success");
      await onLoginSuccess?.();
    },
    retry: 0,
  });

  const signUpErrorHandler = useCallback(
    async (
      error: AuthError,
      { email, password }: { email: string; password: string },
    ) => {
      console.log("❌ signUpErrorHandler", { error });
      if (error.code === "auth/weak-password") {
        toast.error("Password must be at least 6 characters");
        captureAuthBreadcrumb(error, "email-sign-up-weak-password");
        return;
      }
      if (
        error.code === "auth/email-already-in-use" ||
        error.message?.includes("User already exists")
      ) {
        captureAuthBreadcrumb(error, "email-sign-up-existing-user");
        // Check if they can login with the same email and password (maybe they registered but authEndpoint failed)
        loginMutation.mutate({ email, password });

        return;
      }
      if (error.code === "auth/invalid-email") {
        toast.error("That email address is invalid");
        captureAuthBreadcrumb(error, "email-sign-up-invalid-email");
        return;
      }
      if (error.code === "cancelled") {
        return;
      }
      toast.error("Something went wrong, please try again later.");
      captureAuthError(error, "email-sign-up");
    },
    [],
  );

  const socialSignInErrorHandler = useCallback(
    (error: AuthError, credential: { providerId?: string }) => {
      const provider = credential?.providerId;
      toast.error(
        provider === "apple.com" ? "Apple login failed" : "Google login failed",
        {
          description: "Please try again.",
        },
      );
      captureAuthError(error, "social-sign-in", { provider });
    },
    [],
  );

  const socialMutation = useAuthSignInWithCredential(auth, {
    onError: socialSignInErrorHandler,
    retry: 0,
  });

  const signUpMutation = useAuthCreateUserWithEmailAndPassword(auth, {
    onMutate: () => {
      logAuthTiming("firebase-signup-start");
    },
    onError: async (error, variables) => {
      logAuthTiming("firebase-signup-error", { code: error.code });
      await signUpErrorHandler(error, variables);
    },
    onSuccess: () => {
      logAuthTiming("firebase-signup-success");
    },
    retry: 0,
  });

  const loginWithSolanaMutation = useAuthSignInWithCustomToken(auth, {
    onError: (error) => {
      toast.error("Login failed", {
        description: "Please try again.",
      });
      captureAuthError(error, "custom-token-sign-in");
    },
    retry: 0,
  });

  const isSubmitting =
    signUpMutation.isPending ||
    loginMutation.isPending ||
    loginWithSolanaMutation.isPending ||
    status === "db-user-pending";

  const sendForgotEmail = useCallback(async (email: string) => {
    if (!email.length) return;

    toast.success(`We've sent a password reset link to ${email}`);
    session.api.requestPasswordReset
      .mutate({ email: email.trim() })
      .catch((error) => {
        captureException(error, {
          channel: "error",
          area: "auth",
          operation: "request-password-reset",
        });
      });
  }, []);

  const socialLogin = useCallback(
    async (
      credential: AuthCredential | OAuthCredential,
      provider: "google.com" | "apple.com",
    ) => {
      if (provider === "google.com") {
        if (await GoogleSignin.isSignedIn()) {
          await GoogleSignin.signOut();
        }
        credential = credential as AuthCredential;
        socialMutation.mutate(
          GoogleAuthProvider.credential(credential.token, credential.secret),
        );
      } else if (provider === "apple.com") {
        credential = credential as OAuthCredential;
        socialMutation.mutate(credential);
      }
    },
    [],
  );

  const authenticate = useCallback(
    async (
      credential?: AuthCredential | OAuthCredential,
      provider?: "google.com" | "apple.com",
      email?: string,
      password?: string,
      customToken?: string,
    ) => {
      if (credential && provider) {
        return socialLogin(credential, provider);
      }

      if (email && password) {
        authFlowStartedAtRef.current =
          globalThis.performance?.now?.() ?? Date.now();
        logAuthTiming("email-auth-start", {
          emailLength: email.length,
          passwordLength: password.length,
        });
        return signUpMutation.mutate({ email, password });
      }

      if (customToken) {
        await loginWithSolanaMutation.mutateAsync({ token: customToken });
      }
    },
    [],
  );

  useEffect(() => {
    if (!isNavReady) return;
    if (
      !((Platform.OS !== "web" || !isBottomSheetLogin) && !isSetPasswordPath)
    ) {
      return;
    }
    if (status !== "logged-in") return;

    const pendingEmailVerificationToken =
      Platform.OS !== "web" ? getPendingEmailVerificationToken(kv) : null;
    const { target, startSignupJourney } = postAuthDestination({
      onboardingStep,
      signupLike: Boolean(isSignup),
      pendingEmailVerificationToken,
    });
    if (startSignupJourney) {
      useOnboardingStore.getState().setJourney("signup");
    }

    // Belt-and-suspenders: even with the isNavReady gate above, swallow the
    // assertIsReady error if it ever fires here. The useEffect re-runs on
    // dep change and will retry naturally.
    try {
      router.replace(target);
      if (pendingEmailVerificationToken) {
        clearPendingEmailVerificationToken(kv);
      }
    } catch (e) {
      if (
        e instanceof Error &&
        e.message.includes("Attempted to navigate before mounting")
      ) {
        captureException(e, {
          channel: "breadcrumb",
          area: "auth",
          operation: "post-login-redirect-not-ready",
        });
        return;
      }
      throw e;
    }
  }, [
    status,
    isSignup,
    isSetPasswordPath,
    isNavReady,
    isBottomSheetLogin,
    onboardingStep,
  ]);

  return {
    authStatus: status,
    isSubmitting,
    sendForgotEmail,
    authenticate,
  };
};

export default useLoginRegister;
